import { Box, FormHelperText, IconButton, InputBase, InputBaseProps, InputLabel, InputLabelProps } from '@mui/material';
import { RemoveRounded as RemoveRoundedIcon, AddRounded as AddRoundedIcon } from '@mui/icons-material';
import { ChangeEvent, FocusEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useId from '@mui/utils/useId';

export type NumberFieldV2Props = {
  id?: string
  label?: ReactNode
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean,
  error?: boolean
  helperText?: ReactNode
  InputLabelProps?: Omit<InputLabelProps, "children" | "ref">
  InputProps?: Omit<InputBaseProps, "children" | "ref">
}

export default function NumberFieldV2(props: NumberFieldV2Props) {
  const id = useId(props.id)
  const [numberInput, setNumberInput] = useState(String(props.value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setNumberInput(String(props.value));
  }, [props.value]);

  const fixQuantity = useCallback((value: number) => {
    let tmpValue = isNaN(value) ? 0 : value;

    if (props.min !== undefined) {
      tmpValue = Math.max(props.min, tmpValue);
    }

    if (props.max !== undefined) {
      tmpValue = Math.min(props.max, tmpValue);
    }

    return tmpValue;
  }, [props.min, props.max]);

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNumberInput(e.target.value);
  }, []);

  const onInputBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    //Only trigger change if the value changed
    const newValue = fixQuantity(parseInt(e?.target?.value, 0));
    if(newValue === props.value) return;

    props.onChange(newValue);
  }, [props.value, props.onChange, fixQuantity]);

  const onMinus = useCallback(() => {
    const newValue = fixQuantity(props.value - 1);
    if(newValue === props.value) return;

    props.onChange(newValue);
  }, [props.value, props.onChange, fixQuantity]);

  const onPlus = useCallback(() => {
    const newValue = fixQuantity(props.value + 1);
    if(newValue === props.value) return;
    
    props.onChange(newValue);
  }, [props.value, props.onChange, fixQuantity]);

  const { InputProps, InputLabelProps, ...rest } = props;

  return <div>
    {props.label ? <InputLabel
      htmlFor={id}
      error={props.error}
      disabled={props.disabled}
      focused={focused}
      {...InputLabelProps}
    >{props.label}</InputLabel> : <></>}

    <Box
      component="div"
      className={props.disabled ? 'Mui-disabled' : undefined}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
      onFocus={() => {
        setFocused(true);
      }}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        borderRadius: '50px',
        transition: 'background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        flexShrink: 0,
        '&:not(.Mui-disabled)': {
          '&:hover, &:focus-within': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
          '&:has(input:-webkit-autofill)': {
            backgroundColor: '#e8f0fe',
          },
          '&:has(input:-webkit-autofill):hover, &:has(input:-webkit-autofill):focus-within': {
            backgroundColor: '#dce8fb',
          },
        },
      }}
    >
      <IconButton
        onClick={onMinus}
        disabled={props.disabled}
        color="secondary"
        size="small"
        style={{
          borderRadius: 0,
          padding: '5px 10px',
        }}
      ><RemoveRoundedIcon fontSize="small" /></IconButton>
      <InputBase
        id={id}
        value={numberInput}
        onChange={onInputChange}
        onBlur={onInputBlur}
        disabled={props.disabled}
        error={props.error}
        style={{
          background: 'transparent',
          flex: 1,
          width: '3.25rem',
        }}
        sx={{
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset',
            WebkitTextFillColor: 'inherit',
            transition: 'background-color 9999s ease-out 0s',
          },
        }}
        inputProps={{
          style: {
            textAlign: 'center',
            padding: '10px 0',
          }
        }}
      />
      <IconButton
        onClick={onPlus}
        disabled={props.disabled}
        color="secondary"
        size="small"
        style={{
          borderRadius: 0,
          padding: '5px 10px',
        }}
      ><AddRoundedIcon fontSize="small" /></IconButton>
    </Box>
    {props.helperText ? <FormHelperText
      error={props.error}
    >{props.helperText}</FormHelperText> : null}
  </div>
}
